import { KeeperAbstractService } from '../../services/keeper/keeper.abstract.service';
import {
  KeeperActionPrintFile,
  KeeperActionPrintFileErrorConfig,
} from '../../services/keeper/keeper.payload.interface';

interface DownloadHeaders {
  'Content-Type': string;
  'Content-Disposition': string;
  'x-segment-id': string;
  'x-token': string;
  Authorization: string;
  'x-token-type': string;
}

type ServiceMethod = (
  url: string,
  body?: Record<string, unknown>
) => Promise<unknown>;

class FileDownloader {
  private static getAuthData() {
    const anonymousId =
      JSON.parse(localStorage.getItem('ajs_anonymous_id') || '') || '';
    const auth = JSON.parse(localStorage.getItem('auth') || '') || {};
    const userPreference = localStorage.getItem('userPreference') || '';

    return { anonymousId, auth, userPreference };
  }

  private static createHeaders(
    fileName: string,
    fileFormat: string
  ): { headers: DownloadHeaders; userPreference: string } {
    const { anonymousId, auth, userPreference } = FileDownloader.getAuthData();

    const headers: DownloadHeaders = {
      'Content-Type': `application/${fileFormat}`,
      'Content-Disposition': fileName,
      'x-segment-id': anonymousId,
      'x-token': auth.token,
      Authorization: `Bearer ${auth.token}`,
      'x-token-type': auth.tokenType,
    };

    return { headers, userPreference };
  }

  private static downloadFileFallback(url: string, fileName: string): void {
    const anchor = document.createElement('a');
    if (anchor.download !== undefined) {
      anchor.href = url;
      anchor.download = fileName;
      anchor.style.visibility = 'hidden';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } else {
      window.open(url, '_blank');
    }
  }

  private static handlePdfDownload(url: string, fileName: string): void {
    const iframeId = 'pdf-iframe-global';
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      document.body.appendChild(iframe);
    }

    iframe.style.display = 'none';
    iframe.src = url;

    iframe.onload = () => {
      try {
        setTimeout(() => {
          iframe?.focus();
          iframe?.contentWindow?.print();
        }, 1000);
      } catch (error) {
        console.error('Failed to print PDF:', error);
        FileDownloader.downloadFileFallback(url, fileName);
      }
    };
  }

  private static handleError(
    error: unknown,
    bffService: KeeperAbstractService,
    errorConfig?: KeeperActionPrintFileErrorConfig
  ): void {
    if (errorConfig?.method && errorConfig?.url) {
      const httpMethods = ['post', 'patch', 'get', 'delete'] as const;

      if (httpMethods.includes(errorConfig.method as any)) {
        const serviceMethod = bffService[errorConfig.method] as ServiceMethod;

        if (typeof serviceMethod === 'function') {
          // Bind to maintain the bffService context
          serviceMethod.call(bffService, errorConfig.url, {
            data: errorConfig.body,
            error,
          });
        }
      } else {
        console.error(
          `Method "${errorConfig.method}" is not a valid HTTP method for error handling`
        );
      }
    }
  }

  public static async downloadFile(
    bffService: KeeperAbstractService,
    printFile: KeeperActionPrintFile
  ): Promise<void> {
    const { fileName, fileFormat, method, url, body, error } = printFile;
    const { headers, userPreference } = FileDownloader.createHeaders(
      fileName,
      fileFormat
    );

    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...headers,
          'x-user-preferences': userPreference,
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok || response.status === 204) {
        throw new Error(`HTTP status ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: `application/${fileFormat}` })
      );

      if (fileFormat === 'pdf' && isDesktop()) {
        FileDownloader.handlePdfDownload(blobUrl, fileName);
        FileDownloader.revokeObjectURL(blobUrl);
        return;
      }

      FileDownloader.downloadFileFallback(blobUrl, fileName);
      FileDownloader.revokeObjectURL(blobUrl);
    } catch (e) {
      FileDownloader.handleError(e, bffService, error);
    }
  }

  private static revokeObjectURL(url: string): void {
    if (!url) return;
    try {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error('Error revoking object URL:', error);
    }
  }
}

function isDesktop() {
  const ua = navigator.userAgent;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const aspectRatio = width / height;

  // Detecção precisa de navegadores e plataformas
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isMac = /Macintosh/.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS/i.test(ua);
  const isChromeOnIOS = /CriOS/i.test(ua);

  // Detecção especial para iPads (todas gerações)
  const isIPadPro = isMac && touch && navigator.maxTouchPoints > 1;
  const isLegacyIPad = isIOS && !/iPhone|iPod/.test(ua);
  const isSafariOnIPad =
    isSafari && touch && (width > 1024 || aspectRatio < 1.6);

  // Tablets (incluindo todos os iPads e Android)
  const isTablet =
    /(iPad|Tablet|Android(?!.*Mobile)|Kindle|PlayBook|Silk)/i.test(ua) ||
    isIPadPro ||
    isLegacyIPad ||
    isSafariOnIPad ||
    (width >= 768 && aspectRatio < 1.6 && touch);

  if (isTablet) return false;

  // Smartphones (com tratamento especial para iPhones)
  const isIPhone = /iPhone/.test(ua);
  const isAndroidPhone = /Android.*Mobile/.test(ua);
  const isMobileBrowser = /Mobile|IEMobile|BlackBerry|Opera Mini/i.test(ua);

  const isMobile =
    isIPhone ||
    isAndroidPhone ||
    isMobileBrowser ||
    (width < 768 && touch && aspectRatio <= 1.8) ||
    (isChromeOnIOS && touch);

  if (isMobile) return false;

  // Desktop com touch (Surface, All-in-One, etc.)
  const isWindowsTouch = /Windows NT/.test(ua) && touch;
  const isMacTouch = isMac && touch && !isIPadPro;

  const isDesktopTouch =
    touch &&
    !isMobile &&
    !isTablet &&
    (isWindowsTouch || isMacTouch || width > 1024);

  // Dispositivos híbridos (ex: Surface em modo tablet)
  if (isDesktopTouch && width <= 1366 && aspectRatio < 1.4) {
    return false;
  }

  if (typeof window.orientation !== 'undefined') return false;

  return true;
}

export default FileDownloader.downloadFile;
